/**
 * Byte-compatible port of Tabulator's query-string serializer.
 *
 * The backend already speaks the shape Tabulator's `urlGenerator` produced:
 *
 *   page=1&size=10
 *   &sort[0][field]=company&sort[0][dir]=asc
 *   &filter[0][field]=company&filter[0][type]=like&filter[0][value]=acme
 *
 * Ported from node_modules/tabulator-tables/dist/js/tabulator_esm.js
 * (`generateParamsList` / `serializeParams`). Keep it faithful — every
 * deviation is a silent change to what the API receives.
 */

/**
 * Flatten a nested value into {key, value} pairs using Tabulator's bracket
 * notation: arrays index as `prefix[i]`, objects as `prefix[key]`.
 *
 * NOTE (deliberate quirk, do not "fix" here): `typeof new Date() === "object"`
 * and a Date has no own enumerable keys, so a Date value flattens to NOTHING —
 * `filter[i][value]` is omitted entirely. Date filters have therefore always
 * been sent to the API without a value. Preserving that keeps the wire format
 * identical to today. The correct fix is upstream: normalise date filter values
 * to "YYYY-MM-DD" strings before they reach here.
 */
function generateParamsList(data, prefix) {
  let output = [];
  prefix = prefix || "";

  if (Array.isArray(data)) {
    data.forEach((item, i) => {
      output = output.concat(
        generateParamsList(item, prefix ? `${prefix}[${i}]` : String(i))
      );
    });
  } else if (data !== null && typeof data === "object") {
    for (const key in data) {
      output = output.concat(
        generateParamsList(data[key], prefix ? `${prefix}[${key}]` : key)
      );
    }
  } else {
    output.push({ key: prefix, value: data });
  }

  return output;
}

export function serializeParams(params) {
  return generateParamsList(params)
    .map(
      (item) =>
        `${encodeURIComponent(item.key)}=${encodeURIComponent(item.value)}`
    )
    .join("&");
}

/**
 * Append serialised params to a base URL, respecting an existing query string.
 * Mirrors Tabulator's `urlBuilder`.
 */
export function buildUrl(baseUrl, params) {
  const url = (baseUrl || "").trim();
  const qs = serializeParams(params);
  if (!qs) return url;
  return url + (url.includes("?") ? "&" : "?") + qs;
}

/**
 * Build the params object for a browse request. Empty sort/filter arrays
 * produce no output at all, matching Tabulator's behaviour.
 */
export function buildBrowseParams({ page, size, sort, filter, extra }) {
  const params = { page, size };
  if (Array.isArray(sort) && sort.length) params.sort = sort;
  if (Array.isArray(filter) && filter.length) params.filter = filter;
  return Object.assign(params, extra || {});
}
